import { createFileRoute, Link } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import {
  getRevenueOverview,
  exportInvoicesCsv,
} from "@/lib/billing/billing.functions";
import { Button } from "@/components/ui/button";
import { BackButton } from "@/components/BackButton";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { usePermissions } from "@/hooks/use-permissions";
import {
  PermissionLock,
  PermissionLockLoading,
} from "@/components/admin/PermissionLock";

export const Route = createFileRoute("/_authenticated/app/admin/revenue")({
  component: RevenuePage,
  head: () => ({ meta: [{ title: "Revenue — Jozori Admin" }] }),
});

function fmt(cents: number, currency = "USD") {
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(cents / 100);
}

function RevenuePage() {
  const perms = usePermissions();
  const getOverview = useServerFn(getRevenueOverview);
  const exportCsv = useServerFn(exportInvoicesCsv);
  const { data, isLoading } = useQuery({
    queryKey: ["revenue-overview"],
    queryFn: () => getOverview({ data: {} }),
    enabled: !perms.loading && perms.canManageBilling,
  });
  const [downloading, setDownloading] = useState(false);

  async function onExport() {
    setDownloading(true);
    try {
      const { csv } = await exportCsv({ data: {} });
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `jozori-invoices-${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } finally {
      setDownloading(false);
    }
  }

  if (perms.loading) return <PermissionLockLoading />;
  if (!perms.canManageBilling) {
    return (
      <PermissionLock
        titleEn="Finance access required"
        titleAr="صلاحية المالية مطلوبة"
        messageEn="Revenue data is restricted to finance and super admins."
        messageAr="بيانات الإيرادات متاحة لفريق المالية والمسؤول العام فقط."
      />
    );
  }
  if (isLoading) return <div className="p-6">Loading…</div>;
  if (!data) return <div className="p-6">No data</div>;

  return (
    <div className="mx-auto max-w-5xl px-4 py-6 space-y-6">
      <div><BackButton /></div>
      <header className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Revenue</h1>
        <div className="flex gap-2">
          <Link to="/app/admin" className="text-sm text-muted-foreground hover:underline self-center">
            ← Admin
          </Link>
          <Button onClick={onExport} disabled={downloading}>
            {downloading ? "Exporting…" : "Export CSV"}
          </Button>
        </div>
      </header>

      <div className="grid gap-4 sm:grid-cols-3">
        <Card><CardHeader><CardTitle className="text-sm text-muted-foreground">Gross (12 mo)</CardTitle></CardHeader>
          <CardContent className="text-2xl font-bold">{fmt(data.totals.gross_cents)}</CardContent></Card>
        <Card><CardHeader><CardTitle className="text-sm text-muted-foreground">Refunds</CardTitle></CardHeader>
          <CardContent className="text-2xl font-bold">{fmt(data.totals.refunds_cents)}</CardContent></Card>
        <Card><CardHeader><CardTitle className="text-sm text-muted-foreground">Net</CardTitle></CardHeader>
          <CardContent className="text-2xl font-bold">{fmt(data.totals.net_cents)}</CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>Active subscriptions: {data.active_subscriptions}</CardTitle></CardHeader>
        <CardContent>
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr><th className="py-2">Month</th><th>Invoices</th><th>Gross</th><th>Refunds</th><th>Net</th></tr>
            </thead>
            <tbody>
              {data.by_month.map((m: any) => (
                <tr key={m.month} className="border-t">
                  <td className="py-2">{new Date(m.month).toLocaleDateString(undefined,{year:"numeric",month:"short"})}</td>
                  <td>{m.invoice_count}</td>
                  <td>{fmt(Number(m.gross_cents), m.currency)}</td>
                  <td>{fmt(Number(m.refunds_cents), m.currency)}</td>
                  <td className="font-medium">{fmt(Number(m.net_cents), m.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}
